// src/features/media/MediaUtils.ts

import { MediaFileType } from '@lib2/model/media/MediaFileTypes';
import { FileStatusType } from '@lib2/model/media/FileStatusType';
import { MediaStatusType, MediaStateType } from '@lib2/model/media/MediaTypes';
import { bcLong, bcInt } from '@lib2/model/util/string/BaseConverter';
import { MediaTypeUtil } from '@lib2/model/media/MediaType';
import { Hostname, ServiceHosts } from '@lib2/model/common/ServiceHosts';
import { LinkUtils } from '@/features/link/LinkUtils';
import { formatSmartDate, formatTimestamp } from '@/shared/utils/date';
import { MEDIA_STATUS_LABELS, MEDIA_STATUS_COLORS } from '@/lib/constants/media';
import type { SharedLinkConfig } from '@/lib/types/link';
import type { VideoViewData, VideoGridRow, VideoUpdateRequest, IMediaFileData } from '@/lib/types/Media';
import type { VideoFormData } from './schemas/VideoForm.schema';

// Module-level cache — hostname never changes during a session
let _isReseller: boolean | null = null;

/**
 * Utilities for transforming media data for display.
 */
export class MediaUtils {
  /**
   * Find thumbnail file from files array.
   */
  static getThumbnailFile(files: IMediaFileData[]): IMediaFileData | null {
    return files.find(f =>
      f.mediaFileType === MediaFileType.Thumb && f.fileStatusType === FileStatusType.Ready
    ) ?? null;
  }

  /**
   * Find source file for download (video_source or video_output).
   */
  static getSourceFile(files: IMediaFileData[]): IMediaFileData | null {
    // Prefer video_source, fallback to video_output
    const source = files.find(f =>
      f.mediaFileType === MediaFileType.VideoSource && f.fileStatusType === FileStatusType.Ready
    );
    if (source) return source;

    return files.find(f =>
      f.mediaFileType === MediaFileType.VideoOutput && f.fileStatusType === FileStatusType.Ready
    ) ?? null;
  }

  /**
   * Get thumbnail URL for a video.
   */
  static getThumbnailUrl(
    linkConfig: SharedLinkConfig | null,
    video: VideoViewData
  ): string | null {
    if (!linkConfig) return null;

    const thumbFile = MediaUtils.getThumbnailFile(video.files);
    if (!thumbFile) return null;

    return LinkUtils.getMediaFileUrl(
      linkConfig,
      video.media.mediaId,
      thumbFile.fileId,
      thumbFile.format
    );
  }

  /**
   * Get download URL for a single video (used by detail page actions).
   * For batch usage in grids, use toGridRows() which hoists constant computations.
   */
  static getDownloadUrl(
    playerId: number | null,
    video: VideoViewData
  ): string | null {
    if (!playerId) return null;
    if (video.media.mediaStatusType !== MediaStatusType.Ready) return null;

    if (_isReseller === null) _isReseller = Hostname.isResellerHostname();
    const hostname = _isReseller ? ServiceHosts.content.reseller : ServiceHosts.content.streamhoster;
    const cIdBC = bcInt.getStr(video.media.cId);
    const playerIdBC = bcLong.getStr(playerId);
    const mediaIdBC = bcLong.getStr(video.media.mediaId);
    return MediaUtils.buildDownloadUrl(hostname, cIdBC, playerIdBC, mediaIdBC, video.media.mediaType);
  }

  /**
   * Build download URL inline — avoids N object allocations (MediaPlayerLinker).
   * Output matches MediaPlayerLinker.getFileUrl(false, { download: true }) exactly:
   * https://{hostname}/link/http/{cIdBC}/{playerIdBC}/{mediaIdBC}_{mediaTypeId}?dl=1
   */
  private static buildDownloadUrl(
    hostname: string,
    cIdBC: string,
    playerIdBC: string,
    mediaIdBC: string,
    mediaType: string
  ): string {
    const mediaTypeId = MediaTypeUtil.getId(mediaType as Parameters<typeof MediaTypeUtil.getId>[0]);
    return `https://${hostname}/link/http/${cIdBC}/${playerIdBC}/${mediaIdBC}_${mediaTypeId}?dl=1`;
  }

  /**
   * Pre-compute shared constants for grid row transformation.
   * Call once per render cycle, pass to toGridRow() for each visible row.
   */
  static getGridRowContext(videos: VideoViewData[], playerId: number | null) {
    if (_isReseller === null) _isReseller = Hostname.isResellerHostname();
    const hostname = _isReseller ? ServiceHosts.content.reseller : ServiceHosts.content.streamhoster;
    const cId = videos[0]?.media.cId;
    const cIdBC = cId != null ? bcInt.getStr(cId) : '';
    const playerIdBC = playerId ? bcLong.getStr(playerId) : '';
    return { hostname, cIdBC, playerIdBC };
  }

  /**
   * Transform a single video for grid display.
   * Called only for visible rows — NOT all videos.
   * Dates are pre-computed here to avoid Luxon in the render path.
   */
  static toGridRow(
    video: VideoViewData,
    linkConfig: SharedLinkConfig | null,
    playerId: number | null,
    hostname: string,
    cIdBC: string,
    playerIdBC: string
  ): VideoGridRow {
    const media = video.media;
    const displayId = bcLong.getStr(media.mediaId);
    const status = media.mediaStatusType;
    const isReady = status === MediaStatusType.Ready;
    const isActive = media.mediaStateType === MediaStateType.Active;

    return {
      mediaId: media.mediaId,
      displayId,
      title: media.title,
      status,
      state: media.mediaStateType,
      duration: media.duration,
      published: media.published,
      updated: media.updated,
      tags: video.tags,
      thumbnailUrl: MediaUtils.getThumbnailUrl(linkConfig, video),
      downloadUrl: playerId && isReady
        ? MediaUtils.buildDownloadUrl(hostname, cIdBC, playerIdBC, displayId, media.mediaType)
        : null,
      isReady,
      isActive,
      publishedFormatted: media.published ? formatSmartDate(media.published) : null,
      publishedTooltip: media.published ? formatTimestamp(media.published) : null,
      updatedFormatted: formatSmartDate(media.updated),
      updatedTooltip: formatTimestamp(media.updated),
      durationFormatted: MediaUtils.formatDuration(media.duration),
      statusLabel: isReady ? (isActive ? 'Active' : 'Inactive') : MEDIA_STATUS_LABELS[status],
      statusColor: isReady ? (isActive ? 'green' : 'gray') : MEDIA_STATUS_COLORS[status],
      href: `/videos/${displayId}`,
    };
  }

  /**
   * Format duration as HH:MM:SS (always includes hours for visual consistency)
   */
  static formatDuration(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    const pad = (n: number) => n.toString().padStart(2, '0');

    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }

  /**
   * Format seconds as timecode.
   * Returns "MM:SS" for < 1 hour, "HH:MM:SS" for >= 1 hour.
   */
  static formatTimecode(seconds: number): string {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    const pad = (n: number) => n.toString().padStart(2, '0');

    if (h > 0) {
      return `${pad(h)}:${pad(m)}:${pad(s)}`;
    }
    return `${pad(m)}:${pad(s)}`;
  }

  /**
   * Parse timecode string to seconds.
   * Accepts "MM:SS" or "HH:MM:SS" formats.
   * Returns null if invalid.
   */
  static parseTimecode(timecode: string): number | null {
    const parts = timecode.split(':').map((p) => parseInt(p, 10));

    if (parts.some(isNaN)) return null;

    if (parts.length === 2) {
      // MM:SS
      const [m, s] = parts;
      if (m < 0 || s < 0 || s >= 60) return null;
      return m * 60 + s;
    }

    if (parts.length === 3) {
      // HH:MM:SS
      const [h, m, s] = parts;
      if (h < 0 || m < 0 || m >= 60 || s < 0 || s >= 60) return null;
      return h * 3600 + m * 60 + s;
    }

    return null;
  }

  /**
   * Format file size in human-readable form.
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);
    return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  }

  /**
   * Get poster file from files array.
   */
  static getPosterFile(files: IMediaFileData[]): IMediaFileData | null {
    return files.find(f =>
      f.mediaFileType === MediaFileType.Poster && f.fileStatusType === FileStatusType.Ready
    ) ?? null;
  }

  /**
   * Check if a poster file is being processed (not yet ready).
   * Used by ImagesTab to initialize processing state from server data on page load/reload,
   * which in turn enables job polling for processing detection.
   */
  static hasPendingPosterFile(files: IMediaFileData[]): boolean {
    return files.some(f =>
      f.mediaFileType === MediaFileType.Poster &&
      f.fileStatusType !== FileStatusType.Ready &&
      f.fileStatusType !== FileStatusType.Deleted &&
      f.fileStatusType !== FileStatusType.Error
    );
  }

  /**
   * Get total file size from all source/output files.
   */
  static getTotalFileSize(files: IMediaFileData[]): number {
    return files
      .filter(f =>
        (f.mediaFileType === MediaFileType.VideoSource || f.mediaFileType === MediaFileType.VideoOutput)
        && f.fileStatusType === FileStatusType.Ready
      )
      .reduce((sum, f) => sum + f.bytes, 0);
  }

  /**
   * Transform VideoViewData to form data shape.
   * Converts null strings to empty strings for form inputs.
   */
  static transformVideoToFormData(video: VideoViewData): VideoFormData {
    const media = video.media;
    return {
      title: media.title,
      descShort: media.descShort ?? '',
      descLong: media.descLong ?? '',
      refId: media.refId ?? '',
      published: media.published ?? Math.floor(Date.now() / 1000),
      availStart: media.availStart ?? null,
      availEnd: media.availEnd ?? null,
      relatedLinkUrl: media.relatedLinkUrl ?? '',
      relatedLinkText: media.relatedLinkText ?? '',
      mediaStateType: media.mediaStateType as 'active' | 'inactive',
      notes: media.notes ?? '',
      tags: video.tags ?? [],
    };
  }

  /**
   * Transform form data to API update request.
   * Converts empty strings back to null for API.
   */
  static transformFormToUpdateRequest(
    data: VideoFormData,
    refIdChanged: boolean
  ): VideoUpdateRequest {
    return {
      title: data.title,
      descShort: data.descShort || null,
      descLong: data.descLong || null,
      refId: data.refId || null,
      published: data.published,
      availStart: data.availStart,
      availEnd: data.availEnd,
      relatedLinkUrl: data.relatedLinkUrl || null,
      relatedLinkText: data.relatedLinkText || null,
      mediaStateType: data.mediaStateType,
      notes: data.notes || null,
      tags: data.tags,
      adSupported: false,
      refIdChanged,
    };
  }
}
